import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Stagger, staggerItem } from "./FadeIn";

const CALENDLY = "https://calendly.com/katie-fernands/discovery-call";

const steps = [
  {
    title: "Discovery call",
    body: "A thirty-minute conversation about your business, your ideal client, and what growth has looked like so far. We'll see if there's a fit for what I offer and if you'd like to begin.",
  },
  {
    title: "Strategy build",
    body: "I figure out who you're trying to reach, what makes you different, and what you should actually be saying. You leave with a step-by-step plan, not just ideas.",
  },
  {
    title: "Content & outreach",
    body: "We look at where you already show up: LinkedIn, your website, anywhere a prospect might find you. Then we build your plan for how you'll show up going forward, matched to how your prospects actually choose who to work with.",
  },
  {
    title: "Leads & referrals",
    body: "People actually worth talking to start booking time on your calendar. We track what's working, fix what's not, and turn those early wins into something you can count on.",
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-background py-28 md:py-40">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="flex items-center gap-4 mb-20">
          <span className="text-xs uppercase tracking-[0.28em] text-primary">The process</span>
          <span className="h-px flex-1 bg-foreground/15" />
          <span className="text-xs tracking-[0.2em] text-foreground/70">V</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mb-20">
          <h2 className="lg:col-span-8 text-4xl md:text-6xl leading-[1.05] tracking-tight">
            How we do it.
          </h2>
          <p className="lg:col-span-4 lg:pt-4 text-foreground/70 leading-relaxed">
            A four-part rhythm — from first conversation to a pipeline that runs when you're not
            watching.
          </p>
        </div>

        <Stagger className="divide-y divide-foreground/15 border-y border-foreground/15">
          {steps.map((s, i) => (
            <motion.article
              key={s.title}
              variants={staggerItem}
              className="grid lg:grid-cols-12 gap-8 py-10 md:py-14 group"
            >
              <div className="lg:col-span-2 flex items-baseline gap-3">
                <span className="text-5xl md:text-6xl font-serif text-primary tracking-tight leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="lg:col-span-4 text-2xl md:text-3xl leading-tight tracking-tight text-foreground">
                {s.title}
              </h3>
              <p className="lg:col-span-6 text-foreground/70 leading-relaxed text-lg">
                {s.body}
              </p>
            </motion.article>
          ))}
        </Stagger>

        <div className="mt-16">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-medium hover:opacity-90 transition-opacity"
          >
            Start with a discovery call
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
